import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";

// SceneAIReader — reusable AI Reader engine embedded inside Rehearsal / Self-tape.
// Reuses the working POC flow (Deepgram Nova-3 STT + Levenshtein cue-match
// + single-source ElevenLabs playback) but drives turns from a real parsed
// scene passed via props (rehearsal_script.turns).
//
// Props:
//   turns        — [{ character, role: "actor"|"other", line }]
//   sttLanguage  — "pl" | "en" (per-scene, independent from UI lang)
//   voiceId      — optional per-scene voice ID (falls back server-side to env)
//   voiceName    — display-only label ("Marek" for the default voice)
//   sceneId      — optional, for analytics/debug identification

const CUE_LENGTH = 4;
const MATCH_THRESHOLD = 0.5;

// Reader voices for the AI Reader partner. Not secrets — plain frontend config.
// Default = Wiktor (male, PL). Users pick any of these before pressing Start.
const READER_VOICES = [
  { name: "Asia",     gender: "female", voiceId: "Bz1e1clEKwgN71Vx7cxj", slug: "asia" },
  { name: "Wiktor",   gender: "male",   voiceId: "Mmjvne5i15kUnfr4fQF3", slug: "wiktor" },
  { name: "Helena",   gender: "female", voiceId: "0ArNnoIAWKlT4WweaVMY", slug: "helena" },
  { name: "Marcin",   gender: "male",   voiceId: "V5GZ9rfeV9jjKZE5NkT7", slug: "marcin" },
  { name: "Krzysiek", gender: "male",   voiceId: "jJYvw04W4nFnH9II4y4C", slug: "krzysiek" },
  { name: "Gaja",     gender: "female", voiceId: "HNeu7lBpVmKAItS6q4NJ", slug: "gaja" },
  { name: "Tarini",   gender: "female", voiceId: "kiaJRdXJzloFWi6AtFBf", slug: "tarini" },
];
const DEFAULT_VOICE_ID = "Mmjvne5i15kUnfr4fQF3"; // Wiktor

function similarity(a, b) {
  a = (a || "").toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, "").trim();
  b = (b || "").toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, "").trim();
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  const maxLen = Math.max(m, n);
  return 1 - dp[m][n] / maxLen;
}

function lastNWords(text, n) {
  return (text || "").trim().split(/\s+/).slice(-n).join(" ");
}

// Japanese has no whitespace between words. For a JA scene we compare the tail
// of the transcript as a character run against the tail of the target line.
function lastNChars(text, n) {
  const clean = (text || "").replace(/\s+/g, "").normalize("NFC");
  return clean.slice(-n);
}

// Extract distinct alphabetic tokens (length ≥ 3) from all turns to prime
// Deepgram Nova-3 keyterm prompting with scene-specific vocabulary (character
// names, foreign words, titles). Cased original is preserved; dedup is
// case-insensitive; hard cap to keep the WS URL bounded.
function extractKeyterms(turns) {
  const seen = new Map();
  const re = /\p{L}[\p{L}\p{N}'’\-]{2,}/gu;
  for (const t of (turns || [])) {
    const line = t?.line || "";
    let m;
    while ((m = re.exec(line)) !== null) {
      const w = m[0];
      const k = w.toLowerCase();
      if (!seen.has(k)) seen.set(k, w);
    }
  }
  return Array.from(seen.values()).slice(0, 50);
}

export default function SceneAIReader({ turns = [], sttLanguage = "pl", voiceId = null, voiceName = "", sceneId = "", onFreeSessionEnd = null }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  // FREE plan: lifetime 300s session budget (server heartbeat) + on-demand TTS.
  const lifetimeRef = useRef(false);
  const freeActiveRef = useRef(false);
  const freeBeatTimerRef = useRef(null);
  const freeDeadlineRef = useRef(null);
  const freeBeatTsRef = useRef(0);
  const ttsInFlightRef = useRef(new Set());
  const FREE_MSG = lang === "pl"
    ? "Wykorzystałeś 5 darmowych minut AI Readera. Wybierz plan, aby kontynuować pracę z wirtualnym partnerem scenicznym."
    : "You've used your 5 free AI Reader minutes. Choose a plan to keep working with your virtual scene partner.";
  const BUSY_MSG = lang === "pl"
    ? "Na tym koncie trwa już inna darmowa sesja AI Readera."
    : "Another free AI Reader session is already active on this account.";

  const [status, setStatus] = useState("idle"); // idle | connecting | listening | reconnecting | error
  const [transcript, setTranscript] = useState("");
  const [matchScore, setMatchScore] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [cueLocked, setCueLocked] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [turnIdx, setTurnIdx] = useState(0);
  const [sceneDone, setSceneDone] = useState(false);
  const [readerAudio, setReaderAudio] = useState({}); // { [turnIdx]: ObjectURL }
  const [audioReady, setAudioReady] = useState(false);
  const [prepping, setPrepping] = useState(false);
  const [prepErr, setPrepErr] = useState("");
  const [selectedVoiceId, setSelectedVoiceId] = useState(DEFAULT_VOICE_ID);
  const [previewSlug, setPreviewSlug] = useState(null);
  const previewAudioRef = useRef(null);

  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const audioPlayingRef = useRef(false);
  const cueLockedRef = useRef(false);
  const turnIdxRef = useRef(0);
  const statusRef = useRef("idle");
  const readerAudioRef = useRef({});
  const prepInFlightRef = useRef(false);
  // Keep-alive: Deepgram closes idle sockets after ~10s of silence. Sending
  // a KeepAlive JSON frame every 7s keeps the session open across long
  // beats. Cleared on close / stop / unmount to avoid leaked timers.
  const keepAliveTimerRef = useRef(null);
  // Reconnect state: user-initiated stop() sets `stopRequestedRef=true` so
  // `onclose` distinguishes an intentional close from a network drop.
  // `reconnectAttemptsRef` caps auto-recovery at 3 tries (500/1000/2000ms
  // backoff) — after that we surface an error rather than spinning forever.
  const stopRequestedRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  // Deepgram STT session telemetry — reported to backend on close/stop/
  // unmount, fire-and-forget. `sttSessionIdRef` is a fresh UUID PER
  // WebSocket segment so a reconnect creates a new billable session
  // rather than merging seconds across two independent WS connections.
  // `wsOpenTsRef` captures the moment Deepgram accepted the socket; we
  // use it to compute audio_seconds_used. `firstResultTsRef` -> latency.
  const sttSessionIdRef = useRef(null);
  const wsOpenTsRef = useRef(0);
  const firstResultTsRef = useRef(0);
  const sttReportedRef = useRef(false);
  // Deadlock guard: records the turnIdx we already advanced FROM, so that
  // any duplicate `ended` / `error` / play-failure / safety-timeout for the
  // same partner line becomes a no-op. Reset by start().
  const advancedFromRef = useRef(-1);
  // AI Reader STT quota hard-stop. `remaining_seconds` comes from
  // /rehearsal/deepgram-token per segment; a local setTimeout closes the
  // WS the instant the plan cap would be crossed so a single long
  // segment cannot overshoot. Ref cleared at every WS teardown path
  // (onclose, stop, cleanup/unmount, before reconnect) so a stale timer
  // never survives its WebSocket segment.
  const quotaTimerRef = useRef(null);
  // Cumulative STT budget across all WS segments of ONE start() session, so a
  // reconnect can't reuse a stale server `remaining_seconds`.
  const sessionBudgetRef = useRef(null);
  const sessionUsedRef = useRef(0);
  const quotaHitRef = useRef(false);
  // Visible STT quota counter: backend usage + live seconds of the open WS.
  const [sttQuota, setSttQuota] = useState(null);
  const [liveSecs, setLiveSecs] = useState(0);
  const refreshSttQuota = () => {
    api.get("/rehearsal/stt-quota")
      .then((r) => { setSttQuota(r.data); setLiveSecs(0); })
      .catch(() => {});
  };
  const clearQuotaTimer = () => {
    if (quotaTimerRef.current) {
      clearTimeout(quotaTimerRef.current);
      quotaTimerRef.current = null;
    }
  };
  // AI Reader telemetry — one UUID per Reader session (persists across
  // Deepgram WS reconnects, unlike sttSessionIdRef). Fire-and-forget POST
  // to /rehearsal/reader-event; any failure is swallowed. NEVER logs
  // audio, transcript, or scene text — only discrete lifecycle events.
  const readerSessionIdRef = useRef(null);
  const logReaderEvent = (event, extra = {}) => {
    try {
      const sid = readerSessionIdRef.current;
      if (!sid) return;
      const payload = {
        event,
        reader_session_id: sid,
        turnIdx: turnIdxRef.current,
        speaker: turns[turnIdxRef.current]?.role || null,
        status: statusRef.current,
        audioPlaying: audioPlayingRef.current,
        cueLock: cueLockedRef.current,
        ...extra,
      };
      api.post("/rehearsal/reader-event", payload).catch(() => {});
    } catch {}
  };

  useEffect(() => { audioPlayingRef.current = audioPlaying; }, [audioPlaying]);
  useEffect(() => { cueLockedRef.current = cueLocked; }, [cueLocked]);
  useEffect(() => { turnIdxRef.current = turnIdx; }, [turnIdx]);
  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { readerAudioRef.current = readerAudio; }, [readerAudio]);
  useEffect(() => {
    if (status !== "listening") return undefined;
    const id = setInterval(() => {
      const base = lifetimeRef.current ? freeBeatTsRef.current : wsOpenTsRef.current;
      if (base) setLiveSecs((performance.now() - base) / 1000);
    }, 1000);
    return () => clearInterval(id);
  }, [status]);

  const cleanup = () => {
    try { wsRef.current && wsRef.current.close(); } catch {}
    try { mediaRecorderRef.current && mediaRecorderRef.current.stop(); } catch {}
    try { streamRef.current && streamRef.current.getTracks().forEach((tr) => tr.stop()); } catch {}
    if (keepAliveTimerRef.current) {
      clearInterval(keepAliveTimerRef.current);
      keepAliveTimerRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    clearQuotaTimer();
    wsRef.current = null;
    mediaRecorderRef.current = null;
    streamRef.current = null;
    endFreeSession();
  };

  const endFreeSession = () => {
    if (freeBeatTimerRef.current) { clearInterval(freeBeatTimerRef.current); freeBeatTimerRef.current = null; }
    if (freeDeadlineRef.current) { clearTimeout(freeDeadlineRef.current); freeDeadlineRef.current = null; }
    if (!freeActiveRef.current) return;
    freeActiveRef.current = false;
    api.post("/rehearsal/free-session", { session_id: readerSessionIdRef.current, action: "end" })
      .then((r) => { setSttQuota((q) => (q ? { ...q, remaining_seconds: r.data.remaining_seconds } : q)); setLiveSecs(0); })
      .catch(() => {});
  };

  // FREE HARD STOP: kills TTS playback, mic, Deepgram WS, timers, reconnects.
  const hardStop = (reason) => {
    stopRequestedRef.current = true;
    quotaHitRef.current = true;
    try {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.removeAttribute("src"); audioRef.current.load(); }
    } catch {}
    setAudioPlaying(false);
    audioPlayingRef.current = false;
    cleanup();
    setStatus("limit_reached");
    setErrorMsg(reason === "busy" ? BUSY_MSG : FREE_MSG);
    if (reason !== "busy") onFreeSessionEnd?.("exhausted");
  };

  const prepareAudio = async () => {
    // Guard: only one TTS prep may run per component instance at any time.
    // Prevents React 18 StrictMode double-mount, re-renders, or Retry clicks
    // from launching a second parallel prep and bursting past the ElevenLabs
    // Starter concurrency cap (max 6). Batch=4 alone is not enough if two
    // preps overlap (4 + 4 = 8 > 6).
    if (prepInFlightRef.current) return;
    prepInFlightRef.current = true;
    setPrepErr("");
    setPrepping(true);
    setAudioReady(false);
    let q = null;
    try { q = (await api.get("/rehearsal/stt-quota")).data; } catch {}
    if (q) { setSttQuota(q); setLiveSecs(0); }
    lifetimeRef.current = !!q?.lifetime;
    if (lifetimeRef.current) {
      // FREE: no up-front TTS of the whole scene — lines are fetched on demand.
      setReaderAudio({});
      setAudioReady(true);
      setPrepping(false);
      prepInFlightRef.current = false;
      return;
    }
    const otherTurns = (turns || [])
      .map((tt, i) => ({ ...tt, i }))
      .filter((tt) => tt.role === "other" && (tt.line || "").trim());
    if (otherTurns.length === 0) {
      setReaderAudio({});
      setAudioReady(true);
      setPrepping(false);
      prepInFlightRef.current = false;
      return;
    }
    try {
      // Batch to <= 4 concurrent requests to stay under the ElevenLabs Starter
      // concurrency cap (max 6). Each batch resolves fully before the next starts.
      const BATCH = 4;
      const acc = {};
      for (let i = 0; i < otherTurns.length; i += BATCH) {
        const slice = otherTurns.slice(i, i + BATCH);
        const results = await Promise.all(
          slice.map(async (tt) => {
            const body = { text: tt.line, voice_id: selectedVoiceId };
            if (sceneId) body.scene_id = sceneId;
            const r = await api.post("/rehearsal/tts", body, { responseType: "blob" });
            return [tt.i, URL.createObjectURL(r.data)];
          })
        );
        results.forEach(([k, v]) => { acc[k] = v; });
      }
      setReaderAudio(acc);
      setAudioReady(true);
    } catch (e) {
      // If the backend re-raised ElevenLabs' body, it's in response data even for JSON.
      let detail = e?.response?.data?.detail || e?.message || "TTS prep failed";
      if (e?.response?.data instanceof Blob) {
        try { detail = await e.response.data.text(); } catch {}
      }
      setPrepErr(detail);
    } finally {
      setPrepping(false);
      prepInFlightRef.current = false;
    }
  };

  // Auto-prep on mount / when turns change / when voiceId changes.
  useEffect(() => {
    // Revoke any previous URLs before re-prepping.
    Object.values(readerAudioRef.current || {}).forEach((u) => {
      try { URL.revokeObjectURL(u); } catch {}
    });
    setReaderAudio({});
    setTurnIdx(0);
    setSceneDone(false);
    prepareAudio();
    return () => {
      cleanup();
      Object.values(readerAudioRef.current || {}).forEach((u) => {
        try { URL.revokeObjectURL(u); } catch {}
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(turns), selectedVoiceId]);

  // Single source of reader playback.
  useEffect(() => {
    if (status !== "listening") return;
    const tt = turns[turnIdx];
    if (!tt || tt.role !== "other") return;
    const src = readerAudio[turnIdx];
    // No TTS audio for this partner line (empty / failed prep for this
    // turn only). Fall through the standard advancement path so the scene
    // never stalls waiting for an `ended` that will never fire.
    if (!src && lifetimeRef.current) {
      const idx = turnIdx;
      if (ttsInFlightRef.current.has(idx)) return;
      ttsInFlightRef.current.add(idx);
      const body = { text: tt.line, voice_id: selectedVoiceId };
      if (sceneId) body.scene_id = sceneId;
      api.post("/rehearsal/tts", body, { responseType: "blob" })
        .then((r) => setReaderAudio((prev) => ({ ...prev, [idx]: URL.createObjectURL(r.data) })))
        .catch((e) => {
          if (e?.response?.status === 402) hardStop("exhausted");
          else if (turnIdxRef.current === idx) handleAudioEnded();
        })
        .finally(() => ttsInFlightRef.current.delete(idx));
      return;
    }
    if (!src) {
      const skipId = setTimeout(() => handleAudioEnded(), 50);
      return () => clearTimeout(skipId);
    }
    if (!audioRef.current) return;
    const id = setTimeout(() => {
      try {
        audioRef.current.src = src;
        const p = audioRef.current.play();
        // play() returns a Promise on modern browsers. A rejection here
        // (autoplay policy, aborted load, decode error) would otherwise
        // leave `audioPlaying` stuck true and turnIdx frozen — funnel it
        // through the same advancement path used by `ended`.
        if (p && typeof p.catch === "function") p.catch(() => {
          logReaderEvent("partner_audio_error");
          handleAudioEnded();
        });
      } catch { logReaderEvent("partner_audio_error"); handleAudioEnded(); }
    }, 150);
    // Absolute safety fallback: no single partner line should hold the
    // Reader for more than 120s. If neither `ended` nor `error` fires
    // (e.g. TTS blob decode hung mid-stream) the safety timer forces the
    // same advancement path so STT never gets abandoned.
    const safetyId = setTimeout(() => handleAudioEnded(), 120000);
    return () => { clearTimeout(id); clearTimeout(safetyId); };
  }, [turnIdx, status, readerAudio, turns]);

  // Guarantee: whenever the current turn belongs to the actor, `cueLocked`
  // MUST be false. Any exit path from a partner line (normal ended, audio
  // error, play() rejection, missing TTS, safety timeout) funnels through
  // handleAudioEnded, but this effect is the belt-and-suspenders unlock
  // so the actor turn can never begin locked regardless of how the
  // previous partner audio ended.
  // FIX #3 — Also force audioPlaying=false on actor-turn entry. Without
  // this, a reconnect that raced with `onEnded` (or any stray "playing"
  // state carried over from the previous partner turn) would leave
  // audioPlayingRef=true, which gates runCueMatch → the actor speaks and
  // the Reader ignores every cue silently.
  useEffect(() => {
    const tt = turns[turnIdx];
    if (!tt || tt.role !== "actor") return;
    setCueLocked(false);
    cueLockedRef.current = false;
    setMatchScore(0);
    setTranscript("");
    setAudioPlaying(false);
    audioPlayingRef.current = false;
  }, [turnIdx, turns]);

  const runCueMatch = (text) => {
    if (audioPlayingRef.current || cueLockedRef.current) return;
    const tt = turns[turnIdxRef.current];
    if (!tt || tt.role !== "actor") return;
    const target = lastNWords(tt.line, CUE_LENGTH);
    const spoken = lastNWords(text, CUE_LENGTH);
    // Japanese has no spaces — fall back to per-character tails.
    const isJa = (sttLanguage || "").toLowerCase().startsWith("ja");
    const targetCmp = isJa ? lastNChars(tt.line, 8) : target;
    const spokenCmp = isJa ? lastNChars(text, 8) : spoken;
    if (!targetCmp || !spokenCmp) return;
    const score = similarity(spokenCmp, targetCmp);
    setMatchScore(score);
    if (score >= MATCH_THRESHOLD) {
      setCueLocked(true);
      cueLockedRef.current = true;
      setTurnIdx((i) => Math.min(i + 1, turns.length));
    }
  };

  const handleAudioEnded = () => {
    // Dedupe: an `ended` + `error` + safety-timeout can all fire for the
    // same partner line. `advancedFromRef` records the turnIdx we already
    // advanced FROM so only the first caller wins.
    const from = turnIdxRef.current;
    if (advancedFromRef.current === from) return;
    advancedFromRef.current = from;
    setAudioPlaying(false);
    const next = from + 1;
    if (next >= turns.length) {
      setSceneDone(true);
      cleanup();
      setStatus("idle");
      return;
    }
    setTurnIdx(next);
    if (turns[next]?.role === "actor") {
      setCueLocked(false);
      cueLockedRef.current = false;
      setMatchScore(0);
      setTranscript("");
    }
  };

  // Report Deepgram STT usage exactly once per WebSocket segment. Idempotent
  // by session_id on the backend too, but this guard prevents even the
  // POST from firing twice (onclose + cleanup + unmount all funnel here).
  const reportSttUsage = (success) => {
    if (sttReportedRef.current) return;
    const sid = sttSessionIdRef.current;
    if (!sid || !wsOpenTsRef.current) return;
    sttReportedRef.current = true;
    const secs = Math.max(0, (performance.now() - wsOpenTsRef.current) / 1000);
    sessionUsedRef.current += secs;
    const latency = firstResultTsRef.current
      ? Math.max(0, Math.round(firstResultTsRef.current - wsOpenTsRef.current))
      : null;
    // Fire-and-forget — a telemetry failure must NEVER block or delay UX.
    try {
      api.post("/rehearsal/stt-usage", {
        session_id: sid,
        audio_seconds_used: secs,
        latency_ms: latency,
        success: !!success,
        model: "nova-3",
      }).then(refreshSttQuota).catch(() => {});
    } catch {}
  };

  // Open (or re-open) the Deepgram WS + mic pipeline. Extracted from
  // start() so `onclose` can call it again during transparent reconnect
  // without touching component-level scene state (turnIdx, cueLocked,
  // errorMsg, etc.) — the user keeps their place in the scene across a
  // dropped connection.
  const openConnection = async () => {
    // Before opening a new WS segment kill any stale quota timer from a
    // previous segment. New segment → new token → new remaining budget.
    clearQuotaTimer();
    let tokenResp;
    try {
      tokenResp = await api.get("/rehearsal/deepgram-token", { params: { reader_session: readerSessionIdRef.current || "" } });
    } catch (e) {
      if (lifetimeRef.current && (e?.response?.status === 402 || e?.response?.status === 409)) {
        hardStop(e.response.status === 402 ? "exhausted" : "busy");
        return;
      }
      // 402 = plan cap exhausted server-side. Surface the same UI as a
      // client-side hard-stop rather than looking hung on a spinner.
      if (e?.response?.status === 402) {
        setStatus("limit_reached");
        setErrorMsg(t("reh.ai_reader_limit_reached"));
        return;
      }
      setStatus("error");
      setErrorMsg("Token endpoint failed: " + (e?.response?.data?.detail || e.message));
      return;
    }
    const token = tokenResp?.data?.access_token;
    const model = tokenResp?.data?.model || "nova-3";
    // Per-segment remaining STT budget from the plan. Never negative;
    // 0 means server would have raised 402 → treat as limit_reached.
    const _remainingRaw = Number(tokenResp?.data?.remaining_seconds);
    const _serverRemaining = Number.isFinite(_remainingRaw) ? Math.max(0, _remainingRaw) : 0;
    setSttQuota((q) => (q ? { ...q, remaining_seconds: _serverRemaining } : q));
    setLiveSecs(0);
    if (sessionBudgetRef.current === null) sessionBudgetRef.current = _serverRemaining;
    const remainingSeconds = Math.max(0, Math.min(_serverRemaining, sessionBudgetRef.current - sessionUsedRef.current));
    if (remainingSeconds <= 0) {
      setStatus("limit_reached");
      setErrorMsg(t("reh.ai_reader_limit_reached"));
      return;
    }
    if (!token) {
      setStatus("error");
      setErrorMsg("No access_token returned from backend");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
    } catch (e) {
      setStatus("error");
      setErrorMsg("Microphone permission denied: " + e.message);
      return;
    }
    const kt = extractKeyterms(turns);
    const ktQuery = kt.map((tk) => `&keyterm=${encodeURIComponent(tk)}`).join("");
    const url = `wss://api.deepgram.com/v1/listen?model=${encodeURIComponent(model)}&language=${encodeURIComponent(sttLanguage || "pl")}&interim_results=true&punctuate=false${ktQuery}`;
    let ws;
    try {
      ws = new WebSocket(url, ["bearer", token]);
    } catch (e) {
      setStatus("error");
      setErrorMsg("WebSocket construct failed: " + e.message);
      cleanup();
      return;
    }
    wsRef.current = ws;
    // Per-segment session id — reconnect creates a fresh billable session.
    sttSessionIdRef.current =
      (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `stt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    wsOpenTsRef.current = 0;
    firstResultTsRef.current = 0;
    sttReportedRef.current = false;

    ws.onopen = () => {
      const _wasReconnect = reconnectAttemptsRef.current > 0;
      const _reconnectAttemptAt = reconnectAttemptsRef.current;
      setStatus("listening");
      wsOpenTsRef.current = performance.now();
      // Fresh, successful connection — reset the reconnect budget so a
      // brief drop later still has the full 3-attempt allowance.
      reconnectAttemptsRef.current = 0;
      if (_wasReconnect) {
        logReaderEvent("reconnect_success", { reconnectAttempt: _reconnectAttemptAt });
      }
      // Hard-stop quota timer — armed ONLY after the WS is actually open,
      // so token grant / mic prompt / WS handshake latency don't eat the
      // user's remaining budget. Closes the WS (`stopRequestedRef=true`
      // suppresses the reconnect loop); the existing `onclose` path then
      // invokes `reportSttUsage()` on the wsOpenTsRef delta, which is the
      // authoritative segment usage — no double-counting.
      clearQuotaTimer();
      quotaTimerRef.current = setTimeout(() => {
        quotaTimerRef.current = null;
        quotaHitRef.current = true;
        stopRequestedRef.current = true;
        cleanup();
        setStatus("limit_reached");
        setErrorMsg(t("reh.ai_reader_limit_reached"));
      }, Math.max(0, remainingSeconds) * 1000);
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0 && ws.readyState === WebSocket.OPEN) {
          ws.send(ev.data);
        }
      };
      mr.start(250);
      // Deepgram KeepAlive — send a JSON control frame every 7s so a long
      // silent beat never trips their idle-timeout close.
      if (keepAliveTimerRef.current) clearInterval(keepAliveTimerRef.current);
      keepAliveTimerRef.current = setInterval(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          try { wsRef.current.send(JSON.stringify({ type: "KeepAlive" })); } catch {}
        }
      }, 7000);
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type !== "Results") return;
        const text = msg.channel?.alternatives?.[0]?.transcript || "";
        if (!text) return;
        if (!firstResultTsRef.current) {
          firstResultTsRef.current = performance.now();
        }
        setTranscript(text);
        runCueMatch(text);
      } catch {}
    };
    ws.onerror = () => {
      setStatus("error");
      setErrorMsg("WebSocket error (check console)");
      reportSttUsage(false);
    };
    ws.onclose = (ev) => {
      // Kill the quota timer FIRST — it must never outlive its WebSocket
      // segment (a stale timer could otherwise fire during the next
      // segment's grace period and close a fresh WS).
      clearQuotaTimer();
      // Report this segment's usage exactly once — even before we decide
      // whether to reconnect, so a crashed reconnect chain doesn't lose
      // the seconds that ARE billable.
      reportSttUsage(statusRef.current !== "error");
      logReaderEvent("ws_disconnect", { close_code: ev?.code ?? null, close_reason: ev?.reason || "" });
      // FIX #2 — WS dropped mid partner-audio. Without recovery the audio
      // element stays "playing" from the user's POV, audioPlayingRef gates
      // runCueMatch forever, and the playback effect will never fire again
      // for this turnIdx (dep list unchanged). Reset the audio side + the
      // dedupe guard so the SAME partner line safely replays after reconnect
      // when the effect re-runs on status: reconnecting → listening.
      const _curTurn = turns[turnIdxRef.current];
      if (audioPlayingRef.current && _curTurn && _curTurn.role === "other") {
        try {
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
        } catch {}
        setAudioPlaying(false);
        audioPlayingRef.current = false;
        // Sentinel < turnIdx → first ended/error after the replay is allowed.
        advancedFromRef.current = -1;
      }
      // Kill KeepAlive + MediaRecorder — they belong to the WS that just
      // died. We'll spin up fresh ones inside `openConnection()` on retry.
      if (keepAliveTimerRef.current) {
        clearInterval(keepAliveTimerRef.current);
        keepAliveTimerRef.current = null;
      }
      try { mediaRecorderRef.current && mediaRecorderRef.current.stop(); } catch {}
      try { streamRef.current && streamRef.current.getTracks().forEach((tr) => tr.stop()); } catch {}
      mediaRecorderRef.current = null;
      streamRef.current = null;
      wsRef.current = null;
      if (stopRequestedRef.current) {
        if (!quotaHitRef.current) setStatus("idle");
        return;
      }
      if (statusRef.current === "error") return; // onerror already surfaced it
      // Transparent reconnect with progressive backoff. Caps at 3 tries so
      // a genuinely offline user gets a real error instead of a spinner.
      const attempt = reconnectAttemptsRef.current + 1;
      if (attempt > 3) {
        logReaderEvent("reconnect_failed", { reconnectAttempt: attempt });
        setStatus("error");
        setErrorMsg("Utracono połączenie z serwerem STT. Spróbuj ponownie kliknąć Start.");
        return;
      }
      reconnectAttemptsRef.current = attempt;
      setStatus("reconnecting");
      logReaderEvent("reconnect_attempt", { reconnectAttempt: attempt });
      const delay = attempt === 1 ? 500 : attempt === 2 ? 1000 : 2000;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        // Bail if the user stopped in the meantime.
        if (stopRequestedRef.current) { setStatus("idle"); return; }
        openConnection();
      }, delay);
    };
  };

  const start = async () => {
    if (!audioReady || !turns.length) return;
    setErrorMsg("");
    setTranscript("");
    setMatchScore(0);
    setCueLocked(false);
    setTurnIdx(0);
    setSceneDone(false);
    setStatus("connecting");
    stopRequestedRef.current = false;
    reconnectAttemptsRef.current = 0;
    sessionBudgetRef.current = null;
    sessionUsedRef.current = 0;
    quotaHitRef.current = false;
    advancedFromRef.current = -1;
    readerSessionIdRef.current =
      (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `reader-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    if (lifetimeRef.current) {
      let fr;
      try {
        fr = await api.post("/rehearsal/free-session", { session_id: readerSessionIdRef.current, action: "start" });
      } catch (e) {
        const st = e?.response?.status;
        setStatus(st === 402 ? "limit_reached" : "error");
        setErrorMsg(st === 402 ? FREE_MSG : st === 409 ? BUSY_MSG : (e?.response?.data?.detail || e.message));
        if (st === 402) onFreeSessionEnd?.("exhausted");
        return;
      }
      const rem = Math.max(0, Number(fr?.data?.remaining_seconds) || 0);
      freeActiveRef.current = true;
      freeBeatTsRef.current = performance.now();
      setSttQuota((q) => (q ? { ...q, remaining_seconds: rem } : q));
      setLiveSecs(0);
      freeDeadlineRef.current = setTimeout(() => hardStop("exhausted"), rem * 1000);
      freeBeatTimerRef.current = setInterval(() => {
        api.post("/rehearsal/free-session", { session_id: readerSessionIdRef.current, action: "beat" })
          .then((r) => {
            freeBeatTsRef.current = performance.now();
            setSttQuota((q) => (q ? { ...q, remaining_seconds: r.data.remaining_seconds } : q));
            setLiveSecs(0);
            if (r.data.stop) hardStop("exhausted");
          })
          .catch((e) => {
            const st = e?.response?.status;
            if (st === 402) hardStop("exhausted");
            else if (st === 409) hardStop("busy");
          });
      }, 5000);
    }
    logReaderEvent("reader_started");
    await openConnection();
  };

  const stop = () => {
    logReaderEvent("reader_stopped");
    stopRequestedRef.current = true;
    cleanup();
    setStatus("idle");
    if (lifetimeRef.current) onFreeSessionEnd?.("ended");
  };

  // FREE: natural end of the scene (incl. actor's last line) closes the session + paywall.
  useEffect(() => {
    if (!lifetimeRef.current || !turns.length) return;
    if (sceneDone || (freeActiveRef.current && turnIdx >= turns.length)) {
      if (freeActiveRef.current) { stopRequestedRef.current = true; cleanup(); setStatus("idle"); }
      onFreeSessionEnd?.("ended");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneDone, turnIdx]);

  const resetCueLock = () => {
    logReaderEvent("cue_reset");
    setCueLocked(false);
    setMatchScore(0);
    // FIX #1 — Cue reset unblocks the CURRENT turn to advance again. Without
    // this, if a partner line already advanced once (`advancedFromRef` ==
    // turnIdx-1 after audio ended) and the user rewound / re-read the same
    // actor cue, handleAudioEnded's dedupe guard would silently swallow the
    // next legitimate advance. Reset to sentinel so exactly ONE next advance
    // is allowed per fresh cue — the in-turn double-advance protection
    // (guarding ended+error+timeout for a SINGLE playback) still works
    // because it re-arms itself the moment we advance.
    advancedFromRef.current = -1;
  };

  const hasScene = !!(turns && turns.length);
  const activeTurn = turns[turnIdx];

  return (
    <div className="mt-2" data-testid="scene-ai-reader" data-scene-id={sceneId}>
      <p className="text-stone-400 text-xs mb-3">{t("reh.ai_reader_hint")}</p>
      {sttQuota?.cap_seconds > 0 && (
        <div className="mono text-[11px] tracking-widest text-stone-400 uppercase mb-3" data-testid="ai-reader-stt-quota">
          {lang === "pl" ? "Pozostało" : "Remaining"}: {Math.floor(Math.max(0, sttQuota.remaining_seconds - liveSecs) / 60)} / {Math.round(sttQuota.cap_seconds / 60)} min
        </div>
      )}

      <div className="flex gap-2 flex-wrap items-center mb-3">
        {status !== "listening" ? (
          <button
            data-testid="ai-reader-start-btn"
            onClick={start}
            disabled={!audioReady || !hasScene || status === "connecting" || status === "reconnecting"}
            className="pill-btn disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Start
          </button>
        ) : (
          <button data-testid="ai-reader-stop-btn" onClick={stop} className="pill-btn">Stop</button>
        )}
        {status === "reconnecting" && (
          <span
            className="text-amber-400 text-xs mono"
            data-testid="ai-reader-reconnecting"
          >
            {t("reh.ai_reader_reconnecting")}
          </span>
        )}
        <button data-testid="ai-reader-reset-cue-btn" onClick={resetCueLock} className="ghost-btn !text-xs">Reset cue</button>
        <label className="text-stone-500 text-xs mono flex items-center gap-1" data-testid="ai-reader-voice">
          {t("reh.ai_reader_voice")}:
          <select
            data-testid="ai-reader-voice-select"
            value={selectedVoiceId}
            onChange={(e) => setSelectedVoiceId(e.target.value)}
            disabled={prepping || status === "listening"}
            className="bg-stone-900 border border-stone-800 rounded-sm px-2 py-1 text-stone-200 disabled:opacity-50"
          >
            {READER_VOICES.map((v) => (
              <option key={v.voiceId} value={v.voiceId}>{v.name}</option>
            ))}
          </select>
        </label>
        <div className="flex flex-wrap gap-1 items-center" data-testid="ai-reader-voice-previews">
          {READER_VOICES.map((v) => {
            const isPlaying = previewSlug === v.slug;
            const onClick = () => {
              const a = previewAudioRef.current;
              if (!a) return;
              if (isPlaying) {
                try { a.pause(); a.currentTime = 0; } catch {}
                setPreviewSlug(null);
                return;
              }
              try { a.pause(); } catch {}
              const langCode = (lang === "en" ? "en" : "pl");
              a.src = `/voice-previews/${v.slug}-${langCode}.mp3`;
              setPreviewSlug(v.slug);
              a.play().catch(() => setPreviewSlug(null));
            };
            return (
              <button
                key={v.slug}
                type="button"
                data-testid={`ai-reader-preview-${v.slug}`}
                onClick={onClick}
                className="ghost-btn !text-[10px] !py-1 !px-2 mono"
                title={v.name}
              >
                {isPlaying ? "■" : "▶"} {v.name} · {lang === "en" ? "Preview" : "Posłuchaj"}
              </button>
            );
          })}
        </div>
        <audio
          ref={previewAudioRef}
          preload="none"
          onEnded={() => setPreviewSlug(null)}
          onPause={() => { /* no-op — state updated by explicit stop */ }}
          data-testid="ai-reader-preview-audio"
        />
        {audioReady && (
          <span className="text-emerald-400 text-xs mono" data-testid="ai-reader-audio-ready">
            {t("reh.ai_reader_ready")}
          </span>
        )}
        {prepping && (
          <span className="text-stone-400 text-xs mono" data-testid="ai-reader-audio-prepping">
            {t("reh.ai_reader_prepping")}
          </span>
        )}
        {prepErr && !prepping && (
          <>
            <span className="text-red-400 text-xs mono" data-testid="ai-reader-audio-error">{prepErr}</span>
            <button data-testid="ai-reader-retry-tts-btn" onClick={prepareAudio} className="ghost-btn !text-xs">Retry</button>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="paper p-3 md:col-span-1">
          <div className="mono text-[10px] tracking-widest uppercase text-amber-500">Status</div>
          <div className="text-stone-200 text-xs mt-1" data-testid="ai-reader-status">
            {status} · turn={hasScene ? (Math.min(turnIdx, turns.length - 1) + 1) : 0}/{turns.length} · lang={sttLanguage}
          </div>
          {activeTurn && (
            <div className="mt-2 text-[11px] text-stone-400">
              <span className="mono text-amber-500 mr-1">{activeTurn.role === "actor" ? "ACTOR" : "READER"}</span>
              <span className="text-stone-200 font-semibold mr-1">{activeTurn.character}:</span>
              <span>„{activeTurn.line}"</span>
            </div>
          )}
          {errorMsg && (
            <div className="text-red-400 text-[11px] mt-2" data-testid="ai-reader-error">{errorMsg}</div>
          )}
          {sceneDone && (
            <div className="text-emerald-400 text-xs mt-2" data-testid="ai-reader-scene-done">
              {t("reh.ai_reader_scene_done")}
            </div>
          )}
        </div>

        <div className="paper p-3 md:col-span-1">
          <div className="mono text-[10px] tracking-widest uppercase text-amber-500">Live transcript</div>
          <div className="text-stone-100 text-xs mt-1 min-h-[3em] whitespace-pre-wrap" data-testid="ai-reader-transcript">
            {transcript || <span className="text-stone-500 italic">(oczekiwanie na mowę…)</span>}
          </div>
        </div>

        <div className="paper p-3 md:col-span-1">
          <div className="mono text-[10px] tracking-widest uppercase text-amber-500">Match score</div>
          <div className="text-stone-100 text-xs mt-1" data-testid="ai-reader-score">
            {matchScore.toFixed(2)} {matchScore >= MATCH_THRESHOLD && "✓ MATCH"}
          </div>
        </div>
      </div>

      <audio
        ref={audioRef}
        preload="auto"
        onPlay={() => { setAudioPlaying(true); logReaderEvent("partner_audio_start"); }}
        onEnded={() => { logReaderEvent("partner_audio_end"); handleAudioEnded(); }}
        onError={() => { logReaderEvent("partner_audio_error"); handleAudioEnded(); }}
        onPause={() => setAudioPlaying(false)}
        data-testid="ai-reader-audio"
      />
    </div>
  );
}
